import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

interface AuthContextType {
  user: CognitoUser | null;
  userId: string | null;
  email: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Get Cognito configuration from environment variables
const getCognitoConfig = () => {
  const region = import.meta.env.VITE_COGNITO_REGION || 'eu-west-2';
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

  if (!userPoolId || !clientId) {
    console.warn('⚠️  Cognito configuration missing. Please set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID');
    return null;
  }

  return {
    region,
    userPoolId,
    clientId,
  };
};

const cognitoConfig = getCognitoConfig();
const userPool = cognitoConfig
  ? new CognitoUserPool({
      UserPoolId: cognitoConfig.userPoolId,
      ClientId: cognitoConfig.clientId,
    })
  : null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    if (!userPool) {
      setIsLoading(false);
      return;
    }

    const checkSession = async () => {
      try {
        const currentUser = userPool.getCurrentUser();
        if (currentUser) {
          currentUser.getSession(async (err: Error | null, session: any) => {
            if (err) {
              console.error('Session check failed:', err);
              setUser(null);
              setUserId(null);
              setEmail(null);
              setIsLoading(false);
              return;
            }

            if (session && session.isValid()) {
              // Extract user info from ID token
              const idToken = session.getIdToken();
              const payload = idToken.payload;
              const sub = payload.sub;
              const emailFromToken = payload.email;

              setUser(currentUser);
              setUserId(sub);
              setEmail(emailFromToken);
            } else {
              setUser(null);
              setUserId(null);
              setEmail(null);
            }
            setIsLoading(false);
          });
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error checking session:', error);
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  const signUp = async (email: string, password: string): Promise<void> => {
    if (!userPool) {
      throw new Error('Cognito is not configured');
    }

    return new Promise((resolve, reject) => {
      const attributeList = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: email,
        }),
      ];

      userPool.signUp(email, password, attributeList, [], (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  };

  const confirmSignUp = async (email: string, code: string): Promise<void> => {
    if (!userPool) {
      throw new Error('Cognito is not configured');
    }

    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  };

  const signIn = async (email: string, password: string): Promise<void> => {
    if (!userPool) {
      throw new Error('Cognito is not configured');
    }

    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: async (session) => {
          // Extract user info from ID token
          const idToken = session.getIdToken();
          const payload = idToken.payload;
          const sub = payload.sub;
          const emailFromToken = payload.email;

          setUser(cognitoUser);
          setUserId(sub);
          setEmail(emailFromToken);
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  };

  const signOut = async (): Promise<void> => {
    if (user) {
      user.signOut();
    }
    setUser(null);
    setUserId(null);
    setEmail(null);
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!user) {
      return null;
    }

    return new Promise((resolve, reject) => {
      user.getSession((err: Error | null, session: any) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }

        const accessToken = session.getAccessToken().getJwtToken();
        resolve(accessToken);
      });
    });
  };

  const value: AuthContextType = {
    user,
    userId,
    email,
    isAuthenticated: !!user && !!userId,
    isLoading,
    signUp,
    confirmSignUp,
    signIn,
    signOut,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

